import * as React from 'react';
import debounce from 'debounce';

import { inject, observer } from 'mobx-react';
import { removeEmptyKeys } from '@util/ApiUtil';

import DisturbanceTabs from '@disturbance/DisturbanceTabs';
import DisturbanceYberStatus from '@disturbance/DisturbanceYberStatus';
import { Affix, Accordion, AccordionContent, AccordionItem, AccordionHeader, AccordionTitle, Card, Container, Grid, GridColumn, Text } from 'telia-front-react';
import DisturbanceFilter from '@disturbance/DisturbanceFilter';
import DisturbanceFilterBar from '@disturbance/DisturbanceFilterBar';
import DisturbanceHeader from '@disturbance/DisturbanceHeader';
import DisturbanceMap from '@disturbance/map/DisturbanceMap';
import DisturbanceTable from '@disturbance/DisturbanceTable';
import DisturbanceDataModal from '@disturbance/DisturbanceDataModal';

import TranslateStore from '@stores/TranslateStore';
import SsoStore from '@stores/SsoStore';
import ChangeDisturbanceStore from '@stores/ChangeDisturbanceStore';
import IncidentDisturbanceStore from '@stores/IncidentDisturbanceStore';
import ProblemDisturbanceStore from '@stores/ProblemDisturbanceStore';
import FilterStore from '@stores/FilterStore';
import TabStore from '@stores/TabStore';
import { Disturbance, DisturbanceType, DisturbanceTabId } from '@config/DisturbanceConfig';
import { FilterState } from '@config/DisturbanceFilter';
import DisturbanceLocationModal from '@disturbance/DisturbanceLocationModal';
import RouteStore from '@stores/RouteStore';
import DisturbanceModalStore from '@stores/DisturbanceModalStore';

interface DisturbanceProps {
  isMobile: boolean;
  translateStore?: TranslateStore;
  changeDisturbanceStore?: ChangeDisturbanceStore;
  incidentDisturbanceStore?: IncidentDisturbanceStore;
  problemDisturbanceStore?: ProblemDisturbanceStore;
  ssoStore?: SsoStore;
  filterStore?: FilterStore;
  tabStore?: TabStore;
  routeStore?: RouteStore;
  disturbanceModalStore?: DisturbanceModalStore;
}

@inject(
  'isMobile',
  'translateStore',
  'ssoStore',
  'filterStore',
  'tabStore',
  'changeDisturbanceStore',
  'incidentDisturbanceStore',
  'problemDisturbanceStore',
  'routeStore',
  'disturbanceModalStore'
)
@observer
export default class DisturbanceEmployee extends React.Component<DisturbanceProps, { mapIsOpen: boolean }> {
  private fetchTabs = debounce(async (filter: FilterState): Promise<void> => {
    const { tabStore } = this.props;
    await tabStore.fetchTabs(filter);
  }, 500);

  public constructor(props: DisturbanceProps) {
    super(props);
    this.state = {
      mapIsOpen: false
    };
  }

  public async componentDidMount(): Promise<void> {
    const { tabStore, filterStore } = this.props;
    if (Object.keys(this.props.routeStore.decodedParams).length > 0) {
      await this.openModalFromUrl();
    } else {
      await tabStore.setTab(DisturbanceTabId.INCIDENT, filterStore.filter);
    }
  }

  public async componentDidUpdate(prevProps: DisturbanceProps) {
    const {
      filterStore: { filter }
    } = this.props;
    const filterNew = JSON.parse(JSON.stringify(filter));
    if (prevProps.filterStore.filter && this.compareFilterObject(filterNew, prevProps.filterStore.filter)) {
      await this.fetchTabs(filterNew);
    }
  }

  private compareFilterObject(filter: FilterState, prevFilter: FilterState) {
    let filterPrev = JSON.parse(JSON.stringify(prevFilter));
    const filterNew = removeEmptyKeys(filter);
    filterPrev = removeEmptyKeys(filterPrev);
    return JSON.stringify(filterNew) !== JSON.stringify(filterPrev);
  }

  private async openModalFromUrl(): Promise<void> {
    const { tabStore, filterStore } = this.props;
    for (const [key, value] of Object.entries(this.props.routeStore.decodedParams)) {
      if (key === 'informationalId') {
        let type: DisturbanceType;
        const type_code = value.toString().substring(0, 2);
        if (type_code === 'IM' || type_code === 'OP') {
          type = DisturbanceType.IncidentDisturbanceInfo;
          await tabStore.setTab(DisturbanceTabId.INCIDENT, filterStore.filter);
        } else if (type_code === 'CM' || type_code === 'CR') {
          type = DisturbanceType.ChangeDisturbanceInfo;
          await tabStore.setTab(DisturbanceTabId.CHANGE, filterStore.filter);
        } else if (type_code === 'PM') {
          type = DisturbanceType.ProblemDisturbanceInfo;
          await tabStore.setTab(DisturbanceTabId.PROBLEM, filterStore.filter);
        } else {
          await tabStore.setTab(DisturbanceTabId.INCIDENT, filterStore.filter);
          continue;
        }
        const paramDisturbance: Disturbance = {} as Disturbance;
        paramDisturbance.id = value.toString();
        this.props.filterStore.setSingleElement(value.toString());
        await this.props.disturbanceModalStore.openModal(paramDisturbance, type);
      } else {
        await tabStore.setTab(DisturbanceTabId.INCIDENT, filterStore.filter);
      }
    }
  }

  public render(): JSX.Element {
    const {
      isMobile,
      translateStore: { translate },
      filterStore: { filter },
      changeDisturbanceStore,
      incidentDisturbanceStore,
      problemDisturbanceStore,
      ssoStore: { isDealer, customerId, hasIvrRole }
    } = this.props;

    return (
      <Container maxWidth="lg">
        <Text>
          <Grid>
            <GridColumn offset={['md-4', 'lg-3', 'xl-2']} width={['xs-12', 'md-8', 'lg-9', 'xl-10']}>
              <Text>
                <DisturbanceHeader />
                <DisturbanceYberStatus />
                <Card className="text__margin-double" type="light" background="transparent">
                  <Accordion
                    bordered={false}
                    onToggle={(id: string) => {
                      this.setState({ mapIsOpen: id !== null });
                    }}
                  >
                    <AccordionItem id="failures-map">
                      <AccordionHeader>
                        <AccordionTitle>{translate('general.map.minimized')}</AccordionTitle>
                      </AccordionHeader>
                      <AccordionContent>
                        <DisturbanceMap isOpen={this.state.mapIsOpen} />
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </Card>
              </Text>
            </GridColumn>
          </Grid>
          <Grid reverse={['md']} className="text__margin-double">
            <GridColumn width={['xs-12', 'md-8', 'lg-9', 'xl-10']}>
              <Text>
                {isMobile && <DisturbanceFilterBar isDealer={isDealer} customerId={customerId} />}
                <DisturbanceTabs
                  changes={
                    <DisturbanceTable
                      heading={translate('general.changes')}
                      store={changeDisturbanceStore}
                      rows={changeDisturbanceStore.disturbances}
                      type={DisturbanceType.ChangeDisturbanceInfo}
                      employee={false}
                      filter={filter}
                      isDealer={isDealer}
                      hasIvrRole={hasIvrRole}
                      isLoading={changeDisturbanceStore.isLoading}
                    />
                  }
                  incidents={
                    <DisturbanceTable
                      heading={translate('general.incidents')}
                      store={incidentDisturbanceStore}
                      rows={incidentDisturbanceStore.disturbances}
                      type={DisturbanceType.IncidentDisturbanceInfo}
                      employee={false}
                      isDealer={isDealer}
                      hasIvrRole={hasIvrRole}
                      filter={filter}
                      isLoading={incidentDisturbanceStore.isLoading}
                    />
                  }
                  problems={
                    <DisturbanceTable
                      heading={translate('general.problems')}
                      store={problemDisturbanceStore}
                      rows={problemDisturbanceStore.disturbances}
                      type={DisturbanceType.ProblemDisturbanceInfo}
                      employee={true}
                      filter={filter}
                      isDealer={isDealer}
                      hasIvrRole={hasIvrRole}
                      isLoading={problemDisturbanceStore.isLoading}
                    />
                  }
                  filter={filter}
                />
              </Text>
            </GridColumn>
            <GridColumn width={['xs-12', 'md-4', 'lg-3', 'xl-2']}>
              <Affix>
                <DisturbanceFilter isDealer={isDealer} customerId={customerId} />
              </Affix>
            </GridColumn>
          </Grid>
        </Text>
        <DisturbanceDataModal />
        <DisturbanceLocationModal />
      </Container>
    );
  }
}
